import React from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "layouts/goals/components/GoalButton";
import GoalPill from "layouts/goals/components/GoalPill";
import {
  END_REASON_LABELS,
  PTA_STATUSES,
  formatMoney,
  personName,
  stateOf,
  typeOf,
} from "../assets.constants";

const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : "—");

function Detail({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <Grid item xs={12} sm={6}>
      <SuiTypography variant="caption" color="secondary" display="block">
        {label}
      </SuiTypography>
      <SuiTypography
        variant="button"
        fontWeight="medium"
        display="block"
        sx={{ whiteSpace: "pre-line" }}
      >
        {children}
      </SuiTypography>
    </Grid>
  );
}

function AssetDetailsModal({ open, onClose, asset }) {
  if (!asset) return null;
  const type = typeOf(asset.type);
  const state = stateOf(asset.state);
  const specs = asset.specs || {};
  const pta = PTA_STATUSES.find((option) => option.value === specs.pta_status);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <SuiBox display="flex" alignItems="center" gap={1}>
          {asset.name}
          <GoalPill color={state?.color}>{state?.label || asset.state}</GoalPill>
        </SuiBox>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Detail label="Type">{type?.label}</Detail>
          <Detail label="Serial number">{asset.serial_number}</Detail>
          {(type?.specs || []).map(({ key, label }) => (
            <Detail key={key} label={label}>
              {specs[key]}
            </Detail>
          ))}
          <Detail label="PTA status">{specs.pta_status ? pta?.label : null}</Detail>
          <Detail label="IMEIs">{(specs.imeis || []).join("\n")}</Detail>
          <Detail label="Purchased by / from">{asset.purchased_by}</Detail>
          <Detail label="Purchase date">
            {asset.purchase_date && formatDate(asset.purchase_date)}
          </Detail>
          <Detail label="Price">
            {asset.price !== null && formatMoney(asset.price, asset.currency)}
          </Detail>
          <Detail label="Property of">{asset.property_of}</Detail>
          {asset.state === "disposed" && (
            <>
              <Detail label="Disposed">
                {[
                  END_REASON_LABELS[asset.disposal_reason],
                  asset.disposal_date && formatDate(asset.disposal_date),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Detail>
              <Detail label="Given / sold to">{asset.disposed_to}</Detail>
              <Detail label="Sale price">
                {asset.disposal_price !== null && formatMoney(asset.disposal_price, asset.currency)}
              </Detail>
            </>
          )}
          <Detail label="Accessories">{asset.accessories}</Detail>
          <Detail label="Notes">{asset.notes}</Detail>
        </Grid>

        <SuiTypography variant="button" fontWeight="bold" display="block" mt={3} mb={1}>
          Assignment history
        </SuiTypography>
        {asset.assignments.length === 0 ? (
          <SuiTypography variant="caption" color="secondary">
            Never assigned.
          </SuiTypography>
        ) : (
          asset.assignments.map((assignment) => (
            <SuiBox key={assignment.id} py={1} sx={{ borderTop: "1px solid #eef0f4" }}>
              <SuiTypography variant="button" fontWeight="medium" display="block">
                {personName(assignment.user)}
              </SuiTypography>
              <SuiTypography variant="caption" color="text" display="block">
                {formatDate(assignment.assigned_at)} →{" "}
                {assignment.returned_at ? formatDate(assignment.returned_at) : "present"}
                {assignment.end_reason && ` · ${END_REASON_LABELS[assignment.end_reason]}`}
                {assignment.sale_price !== null &&
                  ` for ${formatMoney(assignment.sale_price, asset.currency)}`}
              </SuiTypography>
              {assignment.notes && (
                <SuiTypography
                  variant="caption"
                  color="secondary"
                  display="block"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {assignment.notes}
                </SuiTypography>
              )}
            </SuiBox>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose}>
          Close
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default AssetDetailsModal;
